import { ACCENT_TAKI, normaliserHex } from './branding.js';

/**
 * LA FAMILLE D'ACCENT : quatorze valeurs derivees d'UNE seule couleur.
 *
 * `tokens.css` ne porte pas une couleur d'accent, il en porte quatorze, accordees
 * a la main autour du lime Taki : le survol, la pression, le filet, l'aplat de
 * selection et son filet, le point, l'encre de la famille (posee sur ses
 * aplats clairs), le libelle pose SUR l'accent plein, l'aplat doux et son
 * encre, l'ombre portee, les deux halos de focus. Un client qui choisit du
 * bleu et ne remplace que `--accent` obtient un bouton bleu qui devient LIME au
 * survol, un halo de focus lime autour d'un champ bleu, une selection lime dans
 * une liste bleue : l'accent ne se remplace pas, il se DERIVE.
 *
 * POURQUOI ICI, ET PAS EN CSS : `color-mix()` et les fonctions relatives de CSS
 * couvriraient les melanges, pas le choix de l'encre, qui demande un calcul de
 * CONTRASTE (une encre illisible sur un bouton n'est pas un detail de gout).
 * Un seul endroit calcule, `ThemeProvider` pose, et les composants continuent
 * de ne connaitre que les noms de tokens.
 *
 * POURQUOI UN CAS LIME EN DUR : les quatorze valeurs du lime sont ACCORDEES, pas
 * calculees. Aucune formule ne les retrouve au chiffre pres, et l'environnement
 * par defaut ne doit pas bouger d'un pixel a cause de ce chantier : quand la
 * couleur demandee est le lime, on rend les valeurs de `tokens.css` telles
 * quelles. La derivation ne sert donc qu'aux couleurs CHOISIES par un client.
 *
 * Seul endroit du package ou des couleurs se calculent : ailleurs, un hex en
 * dur dans un composant reste interdit.
 */

/** Les quatorze valeurs d'une famille d'accent, pretes a poser sur une variable CSS. */
export interface FamilleAccent {
  /** La couleur elle-meme (`--accent`) : bouton primaire, etat actif, marqueurs. */
  accent: string;
  /** Le survol (`--accent-hover`). */
  hover: string;
  /** La pression (`--accent-press`). */
  press: string;
  /** Le filet d'un controle a l'accent (`--accent-border`), et l'anneau `--ring`. */
  border: string;
  /** L'aplat tres clair d'une ligne selectionnee (`--accent-tint`). */
  tint: string;
  /** Le filet de cet aplat (`--accent-tint-border`). */
  tintBorder: string;
  /** Le point, assez fonce pour se voir sur du blanc (`--accent-dot`). */
  dot: string;
  /**
   * L'encre de la famille (`--accent-ink`) : le texte pose sur `tint` et
   * `soft`, et les icones qui prennent la couleur au survol. Toujours SOMBRE,
   * parce que ces aplats sont toujours clairs (du blanc melange a l'accent).
   */
  ink: string;
  /**
   * Le libelle pose SUR l'accent plein (`--accent-label`, alias
   * `--primary-foreground`) : le texte du bouton primaire, d'une pastille
   * pleine. Sombre sur un accent clair, clair sur un accent sombre : c'est le
   * seul token choisi par contraste.
   */
  label: string;
  /** L'aplat doux d'un etat survole ou d'une puce (`--accent-soft`). */
  soft: string;
  /** L'encre posee sur cet aplat doux (`--accent-soft-ink`). */
  softInk: string;
  /** L'ombre portee d'un bouton primaire (`--shadow-accent`), teintee de l'accent. */
  shadow: string;
  /** Le halo de focus (`--ring-accent`). */
  ring: string;
  /** Le halo de focus attenue (`--ring-accent-soft`). */
  ringSoft: string;
}

/**
 * La famille du lime, recopiee de `packages/ui/src/styles/tokens.css`. Les tests
 * (`tests/accent.test.mjs`) comparent les deux : si un token bouge d'un cote
 * sans l'autre, l'environnement par defaut changerait de couleur en silence.
 */
const FAMILLE_TAKI: FamilleAccent = {
  accent: ACCENT_TAKI,
  hover: '#c2f235',
  press: '#aee61f',
  border: '#bbe82f',
  tint: '#f4fbe0',
  tintBorder: '#e4f1be',
  dot: '#a6cc2c',
  ink: '#2a3110',
  label: '#1b1b19',
  soft: '#eef6d4',
  softInk: '#8aae2a',
  shadow: '0 0.5rem 1.25rem -0.625rem rgba(150, 190, 40, 0.75)',
  ring: '0 0 0 3px rgba(203, 251, 69, 0.5)',
  ringSoft: '0 0 0 3px rgba(203, 251, 69, 0.4)',
};

/** Un triplet RGB, canaux 0..255. */
export interface CouleurRgb {
  r: number;
  g: number;
  b: number;
}

/** Une couleur en teinte (0..360), saturation et luminosite (0..100). */
export interface CouleurHsl {
  h: number;
  s: number;
  l: number;
}

/**
 * De combien la luminosite s'ecarte pour chaque etat. Une couleur SOMBRE
 * s'eclaircit au lieu de s'assombrir : sur un bleu nuit, un survol plus sombre
 * encore ne se verrait pas.
 */
const ECART_SURVOL = 5;
const ECART_PRESSION = 12;
const ECART_FILET = 8;

/** Au-dessus de cette luminosite, la couleur est « claire » : les etats l'assombrissent. */
const SEUIL_CLAIR = 50;

/** Part de blanc de chaque melange : plus le chiffre est haut, plus l'aplat est pale. */
const BLANC_TINT = 0.85;
const BLANC_TINT_BORDER = 0.7;
const BLANC_SOFT = 0.78;
const BLANC_ENCRE_CLAIRE = 0.92;

/** Luminosite cible du point et de l'encre douce : assez foncees pour se lire sur du blanc. */
const LUMINOSITE_POINT = 48;
const LUMINOSITE_ENCRE_DOUCE = 40;

/** L'encre sombre garde la teinte de l'accent, a saturation reduite (sinon elle vire au noir colore criard). */
const LUMINOSITE_ENCRE_SOMBRE = 13;
const SATURATION_ENCRE_SOMBRE = 0.5;

function borner(valeur: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, valeur));
}

/** Decompose `#rrggbb` en canaux. L'appelant a deja valide la forme. */
function hexVersRgb(hex: string): CouleurRgb {
  return {
    r: Number.parseInt(hex.slice(1, 3), 16),
    g: Number.parseInt(hex.slice(3, 5), 16),
    b: Number.parseInt(hex.slice(5, 7), 16),
  };
}

/** Recompose `#rrggbb` en minuscules, canaux arrondis et bornes. */
export function rgbVersHex({ r, g, b }: CouleurRgb): string {
  const canal = (valeur: number) =>
    Math.round(borner(valeur, 0, 255))
      .toString(16)
      .padStart(2, '0');
  return `#${canal(r)}${canal(g)}${canal(b)}`;
}

/** `#rrggbb` vers teinte / saturation / luminosite. */
export function hexVersHsl(hex: string): CouleurHsl {
  const { r, g, b } = hexVersRgb(hex);
  const rn = r / 255;
  const gn = g / 255;
  const bn = b / 255;
  const max = Math.max(rn, gn, bn);
  const min = Math.min(rn, gn, bn);
  const delta = max - min;
  const l = (max + min) / 2;

  // Un gris n'a ni teinte ni saturation : le dire explicitement evite une
  // division par zero, et rend la derivation d'un accent gris inoffensive.
  if (delta === 0) return { h: 0, s: 0, l: l * 100 };

  const s = delta / (1 - Math.abs(2 * l - 1));
  let h: number;
  if (max === rn) {
    h = ((gn - bn) / delta) % 6;
  } else if (max === gn) {
    h = (bn - rn) / delta + 2;
  } else {
    h = (rn - gn) / delta + 4;
  }
  h *= 60;
  if (h < 0) h += 360;
  return { h, s: s * 100, l: l * 100 };
}

/** Les canaux bruts du sixieme de roue chromatique ou tombe la teinte (avant translation). */
function canauxDuSecteur(secteur: number, c: number, x: number): [number, number, number] {
  switch (secteur) {
    case 0:
      return [c, x, 0];
    case 1:
      return [x, c, 0];
    case 2:
      return [0, c, x];
    case 3:
      return [0, x, c];
    case 4:
      return [x, 0, c];
    default:
      return [c, 0, x];
  }
}

/** Teinte / saturation / luminosite vers `#rrggbb`. */
export function hslVersHex({ h, s, l }: CouleurHsl): string {
  const teinte = ((h % 360) + 360) % 360;
  const saturation = borner(s, 0, 100) / 100;
  const luminosite = borner(l, 0, 100) / 100;
  const c = (1 - Math.abs(2 * luminosite - 1)) * saturation;
  const x = c * (1 - Math.abs(((teinte / 60) % 2) - 1));
  const m = luminosite - c / 2;
  const [r, g, b] = canauxDuSecteur(Math.floor(teinte / 60), c, x);
  return rgbVersHex({ r: (r + m) * 255, g: (g + m) * 255, b: (b + m) * 255 });
}

/** Melange lineaire avec du blanc. `partBlanc` va de 0 (la couleur) a 1 (le blanc). */
export function melangerAvecBlanc(hex: string, partBlanc: number): string {
  const { r, g, b } = hexVersRgb(hex);
  const part = borner(partBlanc, 0, 1);
  const vers = (canal: number) => canal + (255 - canal) * part;
  return rgbVersHex({ r: vers(r), g: vers(g), b: vers(b) });
}

/** La luminance relative WCAG d'une couleur (0 = noir, 1 = blanc). */
export function luminanceRelative(hex: string): number {
  const { r, g, b } = hexVersRgb(hex);
  const canal = (valeur: number) => {
    const v = valeur / 255;
    return v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * canal(r) + 0.7152 * canal(g) + 0.0722 * canal(b);
}

/** Le rapport de contraste WCAG entre deux couleurs, de 1 (identiques) a 21 (noir sur blanc). */
export function contrasteWcag(hexA: string, hexB: string): number {
  const a = luminanceRelative(hexA);
  const b = luminanceRelative(hexB);
  const clair = Math.max(a, b);
  const sombre = Math.min(a, b);
  return (clair + 0.05) / (sombre + 0.05);
}

/** L'encre sombre teintee de l'accent : celle des aplats clairs, et du libelle quand l'accent est clair. */
function encreSombre(hsl: CouleurHsl): string {
  return hslVersHex({
    h: hsl.h,
    s: hsl.s * SATURATION_ENCRE_SOMBRE,
    l: LUMINOSITE_ENCRE_SOMBRE,
  });
}

/**
 * Le libelle pose SUR l'accent plein. Deux candidates seulement, parce qu'un
 * bouton primaire n'a que deux allures acceptables : texte presque noir teinte
 * de l'accent, ou texte presque blanc teinte de l'accent. On garde celle qui se
 * LIT, pas celle qui plait : c'est le seul token dont une erreur rend un bouton
 * inutilisable. L'encre des aplats clairs (`ink`) ne passe pas par ce choix :
 * un bleu nuit veut un libelle clair sur son bouton, et une encre sombre sur sa
 * ligne selectionnee, qui est presque blanche.
 */
function libelleSurAccent(accent: string, hsl: CouleurHsl): string {
  const sombre = encreSombre(hsl);
  const claire = melangerAvecBlanc(accent, BLANC_ENCRE_CLAIRE);
  return contrasteWcag(sombre, accent) >= contrasteWcag(claire, accent) ? sombre : claire;
}

/**
 * Derive la famille complete depuis une couleur d'accent. Rend `null` si
 * l'entree n'est pas un hex (la config de deploiement accepte `rgb()` et
 * `hsl()`, la derivation non : l'appelant garde alors les tokens du socle).
 */
export function deriverAccent(hex: string): FamilleAccent | null {
  const accent = normaliserHex(hex);
  if (accent === null) return null;
  if (accent === ACCENT_TAKI) return FAMILLE_TAKI;

  const hsl = hexVersHsl(accent);
  // Une couleur sombre n'a plus de marge vers le bas : ses etats montent.
  const sens = hsl.l < SEUIL_CLAIR ? 1 : -1;
  const decale = (ecart: number) => hslVersHex({ ...hsl, l: hsl.l + sens * ecart });

  const dot = hsl.l > SEUIL_CLAIR ? hslVersHex({ ...hsl, l: LUMINOSITE_POINT }) : accent;
  const canaux = hexVersRgb(dot);
  const { r, g, b } = hexVersRgb(accent);

  return {
    accent,
    hover: decale(ECART_SURVOL),
    press: decale(ECART_PRESSION),
    border: decale(ECART_FILET),
    tint: melangerAvecBlanc(accent, BLANC_TINT),
    tintBorder: melangerAvecBlanc(accent, BLANC_TINT_BORDER),
    dot,
    ink: encreSombre(hsl),
    label: libelleSurAccent(accent, hsl),
    soft: melangerAvecBlanc(accent, BLANC_SOFT),
    softInk:
      hsl.l > LUMINOSITE_ENCRE_DOUCE
        ? hslVersHex({ ...hsl, l: LUMINOSITE_ENCRE_DOUCE })
        : accent,
    // L'ombre porte le POINT, pas l'accent : sous un bouton, l'accent pur
    // bave, sa version foncee se lit comme une ombre.
    shadow: `0 0.5rem 1.25rem -0.625rem rgba(${canaux.r}, ${canaux.g}, ${canaux.b}, 0.75)`,
    ring: `0 0 0 3px rgba(${r}, ${g}, ${b}, 0.5)`,
    ringSoft: `0 0 0 3px rgba(${r}, ${g}, ${b}, 0.4)`,
  };
}

/**
 * La famille, sous les noms exacts de `tokens.css` : ce que `ThemeProvider`
 * pose sur `document.documentElement`. `--primary-foreground` n'est pas ici,
 * c'est un alias (`var(--accent-label)`) qui suit tout seul.
 */
export function variablesDAccent(famille: FamilleAccent): Record<string, string> {
  return {
    '--accent': famille.accent,
    '--accent-hover': famille.hover,
    '--accent-press': famille.press,
    '--accent-border': famille.border,
    '--accent-tint': famille.tint,
    '--accent-tint-border': famille.tintBorder,
    '--accent-dot': famille.dot,
    '--accent-ink': famille.ink,
    '--accent-label': famille.label,
    '--accent-soft': famille.soft,
    '--accent-soft-ink': famille.softInk,
    '--shadow-accent': famille.shadow,
    '--ring-accent': famille.ring,
    '--ring-accent-soft': famille.ringSoft,
  };
}
